// services/lis.js - API service for DycoLIS Laboratory Information System
// Handles lab departments, test catalog, orders, specimens, results, reports, dashboard
import api from './api';

const LIS_PREFIX = '/lis';

// =====================================================
// DASHBOARD
// =====================================================
export const lisDashboardService = {
  getStats: () => api.get(`${LIS_PREFIX}/dashboard/stats`),
  getSamplesStats: () => api.get(`${LIS_PREFIX}/dashboard/samples-stats`),
  getResultsStats: () => api.get(`${LIS_PREFIX}/dashboard/results-stats`),
  getTodaySamples: (limit) => api.get(`${LIS_PREFIX}/dashboard/today-samples`, { params: { limit } })
};

// =====================================================
// LAB DEPARTMENTS
// =====================================================
export const labDepartmentService = {
  list: (includeInactive) => api.get(`${LIS_PREFIX}/departments`, { params: { active: includeInactive ? 'false' : undefined } }),
  getById: (id) => api.get(`${LIS_PREFIX}/departments/${id}`),
  create: (data) => api.post(`${LIS_PREFIX}/departments`, data),
  update: (id, data) => api.put(`${LIS_PREFIX}/departments/${id}`, data),
  delete: (id) => api.delete(`${LIS_PREFIX}/departments/${id}`)
};

// =====================================================
// TEST CATALOG
// =====================================================
export const testCatalogService = {
  search: (params) => api.get(`${LIS_PREFIX}/tests`, { params }),
  getById: (id) => api.get(`${LIS_PREFIX}/tests/${id}`),
  create: (data) => api.post(`${LIS_PREFIX}/tests`, data),
  update: (id, data) => api.put(`${LIS_PREFIX}/tests/${id}`, data),
  deactivate: (id) => api.delete(`${LIS_PREFIX}/tests/${id}`)
};

// =====================================================
// LAB ORDERS
// =====================================================
export const labOrderService = {
  create: (data) => api.post(`${LIS_PREFIX}/orders`, data),
  list: (params) => api.get(`${LIS_PREFIX}/orders`, { params }),
  getById: (id) => api.get(`${LIS_PREFIX}/orders/${id}`),
  updateStatus: (id, status, reason) => api.patch(`${LIS_PREFIX}/orders/${id}/status`, { status, reason }),
  uploadReferral: (id, data) => api.patch(`${LIS_PREFIX}/orders/${id}/referral`, data),
  cancel: (id, reason) => api.delete(`${LIS_PREFIX}/orders/${id}`, { data: { reason } })
};

// =====================================================
// SPECIMENS
// =====================================================
export const specimenService = {
  // Collect Sample modal: preview grouping
  previewCollection: (orderId) => api.post(`${LIS_PREFIX}/specimens/preview`, { orderId }),
  // Confirm collection (from modal)
  collectSpecimens: (data) => api.post(`${LIS_PREFIX}/specimens/collect`, data),
  // Samples Queue listing
  list: (params) => api.get(`${LIS_PREFIX}/specimens`, { params }),
  getById: (id) => api.get(`${LIS_PREFIX}/specimens/${id}`),
  accept: (id, notes) => api.patch(`${LIS_PREFIX}/specimens/${id}/accept`, { notes }),
  reject: (id, reason, notes) => api.patch(`${LIS_PREFIX}/specimens/${id}/reject`, { reason, notes }),
  getRejectionReasons: () => api.get(`${LIS_PREFIX}/specimens/rejection-reasons`),
  generateIds: (count) => api.post(`${LIS_PREFIX}/specimens/generate-ids`, { count })
};

// =====================================================
// RESULTS
// =====================================================
export const labResultService = {
  // Enter / save result
  saveResult: (data) => api.post(`${LIS_PREFIX}/results`, data),
  // Results list
  list: (params) => api.get(`${LIS_PREFIX}/results`, { params }),
  getById: (id) => api.get(`${LIS_PREFIX}/results/${id}`),
  // Get result entry template for an order item
  getForEntry: (orderItemId) => api.get(`${LIS_PREFIX}/results/for-entry/${orderItemId}`),
  // Get amendment history
  getHistory: (id) => api.get(`${LIS_PREFIX}/results/${id}/history`)
};

// =====================================================
// REPORTS
// =====================================================
export const labReportService = {
  // Get report for a result
  getForResult: (resultId) => api.get(`${LIS_PREFIX}/reports/for-result/${resultId}`),
  // Get report for a PACS study
  getForStudy: (studyUID) => api.get(`${LIS_PREFIX}/reports/for-study/${studyUID}`),
  // Get all reports for a patient
  getForPatient: (patientId) => api.get(`${LIS_PREFIX}/reports/for-patient/${patientId}`),
  getById: (id) => api.get(`${LIS_PREFIX}/reports/${id}`),
  // Edit (creates new version)
  amend: (id, data) => api.put(`${LIS_PREFIX}/reports/${id}`, data),
  // PDF download data
  getPdf: (id) => api.get(`${LIS_PREFIX}/reports/${id}/pdf`),
  // Share via email
  email: (id, data) => api.post(`${LIS_PREFIX}/reports/${id}/email`, data),
  // Get all versions
  getVersions: (id) => api.get(`${LIS_PREFIX}/reports/${id}/versions`)
};

// =====================================================
// REFERENCE RANGES
// =====================================================
export const referenceRangeService = {
  list: (params) => api.get(`${LIS_PREFIX}/reference-ranges`, { params }),
  create: (data) => api.post(`${LIS_PREFIX}/reference-ranges`, data),
  update: (id, data) => api.put(`${LIS_PREFIX}/reference-ranges/${id}`, data)
};

// =====================================================
// CRITICAL VALUES
// =====================================================
export const criticalValueService = {
  list: () => api.get(`${LIS_PREFIX}/critical-values`),
  update: (id, data) => api.put(`${LIS_PREFIX}/critical-values/${id}`, data)
};

// =====================================================
// AUDIT
// =====================================================
export const labAuditService = {
  list: (params) => api.get(`${LIS_PREFIX}/audit`, { params }),
  getEntityHistory: (entityType, entityId) => api.get(`${LIS_PREFIX}/audit/entity/${entityType}/${entityId}`)
};

// =====================================================
// ANALYZER DEVICES
// =====================================================
export const analyzerService = {
  list: (params) => api.get(`${LIS_PREFIX}/analyzers`, { params }),
  getById: (id) => api.get(`${LIS_PREFIX}/analyzers/${id}`),
  create: (data) => api.post(`${LIS_PREFIX}/analyzers`, data),
  update: (id, data) => api.put(`${LIS_PREFIX}/analyzers/${id}`, data),
  delete: (id) => api.delete(`${LIS_PREFIX}/analyzers/${id}`),
  getMessages: (id, params) => api.get(`${LIS_PREFIX}/analyzers/${id}/messages`, { params }),
  getMessage: (id, messageId) => api.get(`${LIS_PREFIX}/analyzers/${id}/messages/${messageId}`),
  testConnection: (id) => api.post(`${LIS_PREFIX}/analyzers/${id}/test-connection`),
  ingest: (data) => api.post(`${LIS_PREFIX}/analyzers/ingest`, data),
  getListenerStatus: () => api.get(`${LIS_PREFIX}/analyzers/listener/status`),
  mapTest: (id, data) => api.post(`${LIS_PREFIX}/analyzers/${id}/map-test`, data),
  // Virtual Analyzer Simulation
  simulate: (id, specimenIds) => api.post(`${LIS_PREFIX}/analyzers/${id}/simulate`, { specimenIds }),
  registerVirtual: () => api.post(`${LIS_PREFIX}/analyzers/register-virtual`)
};

// =====================================================
// CROSS-SYSTEM REFERRALS (PACS ↔ LIS)
// =====================================================
export const referralService = {
  /** Called by PACS radiologist to request a lab test */
  requestLabTest: (data) => api.post(`${LIS_PREFIX}/referrals/request-lab-test`, data),
  /** Called by LIS user to accept a referral notification */
  acceptReferral: (notificationId) => api.post(`${LIS_PREFIX}/referrals/accept`, { notificationId }),
  /** Called by LIS to send completed report back to PACS */
  sendReport: (data) => api.post(`${LIS_PREFIX}/referrals/send-report`, data),
  /** Get pending referral notifications for current LIS user */
  getPending: () => api.get(`${LIS_PREFIX}/referrals/pending`)
};
